/**
 * Confirmación "Retirar feriado" (008 · T075 · US4; contracts/ui.md §1.5). `POST
 * /holidays/{id}/remove` no lleva cuerpo (A20: `{}`), así que no se pide motivo: la auditoría
 * `HOLIDAY_REMOVED` registra quién y cuándo. El feriado no se borra: queda como "Retirado" y sus
 * días vuelven a evaluarse normalmente. `409 PERIOD_CLOSED` / `INTEGRATION_MANAGED` se explican
 * dentro del diálogo.
 */
import { useState } from 'react';

import type { Holiday } from '@fermin/contracts';

import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent } from '../../../components/ui/dialog';
import { useToast } from '../../../components/ui/toast';
import { t } from '../../../i18n';
import { readOnlyProps, useReadOnly } from '../../shell/use-read-only';
import { scheduleErrorMessage, useRemoveHoliday } from '../api';
import { formatLocalDay } from '../format';

import { holidayScopeLabel } from './holidays-format';

export interface RemoveHolidayDialogProps {
  holiday: Holiday | null;
  onClose: () => void;
}

/** El contenido se monta por feriado: el error de un intento anterior no se arrastra. */
export function RemoveHolidayDialog({ holiday, onClose }: RemoveHolidayDialogProps) {
  return (
    <Dialog open={holiday !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      {holiday ? (
        <RemoveHolidayContent key={holiday.id} holiday={holiday} onClose={onClose} />
      ) : null}
    </Dialog>
  );
}

function RemoveHolidayContent({ holiday, onClose }: { holiday: Holiday; onClose: () => void }) {
  const { toast } = useToast();
  const { readOnly } = useReadOnly();
  const remove = useRemoveHoliday();
  const [error, setError] = useState<string | null>(null);

  function confirm() {
    remove.mutate(
      { id: holiday.id },
      {
        onSuccess: (saved) => {
          toast({
            title: t('schedules.holidays.toast.removed', { name: saved.name }),
            variant: 'success',
          });
          onClose();
        },
        onError: (apiError) => setError(scheduleErrorMessage(apiError)),
      },
    );
  }

  return (
    <DialogContent
      title={t('schedules.holidays.removeDialog.title', { name: holiday.name })}
      description={t('schedules.holidays.removeDialog.description', {
        day: formatLocalDay(holiday.day),
        scope: holidayScopeLabel(holiday),
      })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={remove.isPending}>
            {t('common.buttons.back')}
          </Button>
          <Button
            variant="destructive"
            data-testid="holiday-remove-confirm"
            loading={remove.isPending}
            loadingText={t('common.states.saving')}
            onClick={confirm}
            {...readOnlyProps(readOnly)}
          >
            {t('schedules.holidays.removeDialog.confirm')}
          </Button>
        </>
      }
    >
      <p className="text-fm-sm text-fm-text-secondary">
        {t('schedules.holidays.removeDialog.notice')}
      </p>
      {error ? (
        <p role="alert" className="mt-fm-3 text-fm-sm text-fm-danger-text">
          {error}
        </p>
      ) : null}
    </DialogContent>
  );
}
